"use client";

import React from "react";
import { Card, CardBody } from "@heroui/react";

import { logger } from "@/telemetry";

export interface ImageViewerProps {
  /** Image document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Image filename for display purposes */
  filename?: string;
}

/**
 * Simple Image Viewer component
 * Displays the image only - no text matching or highlighting
 *
 * NOTE: For images, citations are LLM-generated descriptions, not text in the image.
 * Therefore, we just show the original image without any highlighting.
 */
export const ImageViewer: React.FC<ImageViewerProps> = ({ documentData, filename }) => {
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Convert ArrayBuffer to image URL
  React.useEffect(() => {
    if (!documentData) {
      return;
    }

    try {
      // Determine MIME type from filename extension
      const ext = filename?.toLowerCase().split(".").pop() || "png";
      const mimeTypeMap: Record<string, string> = {
        png: "image/png",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        gif: "image/gif",
        webp: "image/webp",
        bmp: "image/bmp",
        svg: "image/svg+xml",
      };

      const mimeType = mimeTypeMap[ext] || "image/png";

      // Create blob from ArrayBuffer
      const blob = new Blob([documentData], { type: mimeType });
      const url = URL.createObjectURL(blob);

      setImageUrl(url);
      setError(null);

      // Cleanup function to revoke object URL
      return () => {
        URL.revokeObjectURL(url);
      };
    } catch (err) {
      logger.error("❌ Failed to create image URL", err);
      setError("Failed to load image");
    }
  }, [documentData, filename]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-danger-500">{error}</p>
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-default-500">Loading image...</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[75vh] overflow-auto bg-content1 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Image Display - No citation text needed since citations are LLM descriptions */}
        <Card>
          <CardBody className="p-4">
            <img
              alt={filename || "Citation image"}
              className="w-full h-auto object-contain"
              src={imageUrl}
              style={{ maxHeight: "60vh" }}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ImageViewer;
